import * as React from "react";
import Card from "@mui/material/Card";
import OutlinedInput from "@mui/material/OutlinedInput";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Unstable_Grid2";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import {useState} from "react";
import {Autocomplete, TextField} from "@mui/material";
import Chip from "@mui/material/Chip";
import Organisation from "benchmarking-domain/organisation";
import {UserSelect} from "../../core/shared/user-select";

interface OrganisationFormProps {
  organisation?: Organisation;
  onSubmit: (data: Organisation) => Promise<void>;
}

export function OrganisationForm({organisation, onSubmit}: OrganisationFormProps): React.JSX.Element {
  const [prevOrganisation, setPrevOrganisation] = useState<Organisation | undefined>(organisation);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [name, setName] = useState<string>(organisation?.name ?? "");
  const [domains, setDomains] = useState<string[]>(organisation?.domains ?? []);
  const [managers, setManagers] = useState<string[]>(organisation?.managers ?? []);

  if(prevOrganisation !== organisation) {
    setPrevOrganisation(organisation);
    setName(organisation?.name ?? "");
    setDomains(organisation?.domains ?? []);
    setManagers(organisation?.managers ?? []);
  }

  const saveData = async ():Promise<void> => {
    setIsSaving(true);

    try {
      await onSubmit({ ...organisation, id: organisation?.id ?? "", domains, name, managers });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        await saveData();
      }}
    >
      <Card>
        <CardHeader subheader="The information can be edited" title="Organisation"/>
        <Divider/>
        <CardContent>
          <Grid container spacing={3}>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Name</InputLabel>
                <OutlinedInput onChange={(e) => {
                  setName(e.target.value);
                }} value={name} label="Name" name="name"/>
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <Autocomplete
                  multiple
                  id="domains-filled"
                  options={[]}
                  freeSolo
                  autoSelect
                  value={domains}
                  onChange={(e, value) => {
                    setDomains(value);
                  }}
                  renderTags={(value: string[], getTagProps) =>
                    value.map((option: string, index: number) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({index})}
                        key={option}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Domains"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <UserSelect
                id="managers-filled"
                organisationId={organisation?.id}
                label="Managers"
                value={managers}
                onChange={setManagers}
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider/>
        <CardActions sx={{justifyContent: 'flex-end'}}>
          <Button type="submit" variant="contained" disabled={isSaving}>Save details</Button>
        </CardActions>
      </Card>
    </form>
  );
}
